import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Mutation, Query, Subscription } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Message } from '@adornis/mails/server/communication.js';
import { emailRegexCheck } from '@adornis/users/db/validators.js';
import { getUserFromContext } from '@adornis/users/server/util.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { validateIban } from '@adornis/validation/functions/iban.js';
import { Versioned } from '@adornis/versioned-entity/db/versioned-mongo-entity.js';
import { ChancenwerkCounter } from '@chancenwerk/db/counters.js';
import type { Weekday } from '@chancenwerk/db/enums/enums.js';
import { MailEventLog, MailEventLogType } from '@chancenwerk/db/eventlogs/MailEventlog.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import { ChancenRole } from '@chancenwerk/db/semantics/ChancenRole.js';
import { Role } from '@chancenwerk/db/semantics/Role.js';
import { SemanticRelationship } from '@chancenwerk/db/semantics/SemanticRelationship.js';
import { Taetigkeit } from '@chancenwerk/db/semantics/Taetigkeit.js';
import { CRVertrag } from '@chancenwerk/master-data-management/users/entities/CRVertrag.js';
import { registerEmployee } from '@chancenwerk/server/mailTemplates.js';
import { getMitarbeiterFromSoftgardenKeyFromCache } from '@chancenwerk/server/softgardenImport.js';
import { DateTime } from 'luxon';
import { from, mapTo, switchMap, type Observable } from 'rxjs';
import { ChancenwerkUser } from './ChancenwerkUser.js';

export enum Vertragsarten {
  ANSTELLUNG = 'Anstellung',
  EHRENAMTLER = 'Ehrenamtler (bis 840 Euro)',
  MINIJOB = 'Minijob',
  PRAKTIKUM = 'Praktikum',
  UEBUNGSLEITER = 'Übungsleiter (bis 3.000 Euro)',
  WERKSTUDENT = 'Werkstudent',
  VERWALTUNGSMITARBEITER = 'VerwaltungsMitarbeiter',
}

/**
 * @entity WorkAvailablility
 */
@Entity()
export class WorkAvailablility extends AdornisEntity {
  public static override _class = 'WorkAvailablility';

  public static override resolveID(entity: any): string {
    return entity.weekday;
  }

  @Field(type => String)
  public weekday: Maybe<Weekday>;

  @Field(type => DateTime)
  public from: Maybe<DateTime>;

  @Field(type => DateTime)
  public to: Maybe<DateTime>;
}

/**
 * @entity WorkshopsAndTrainingInformation
 */
@Entity()
export class WorkshopsAndTrainingInformation extends AdornisEntity {
  public static override _class = 'WorkshopsAndTrainingInformation';

  // OS Workshops
  @Field(type => DateTime)
  public participatedOSWorkshopOneRoleModel: Maybe<DateTime>;

  @Field(type => DateTime)
  public carriedOutOSWorkshopOneRoleModel: Maybe<DateTime>;

  @Field(type => DateTime)
  public participatedOSWorkshopTwoChallenges: Maybe<DateTime>;

  @Field(type => DateTime)
  public carriedOutOSWorkshopTwoChallenges: Maybe<DateTime>;

  // SuS Workshops
  @Field(type => DateTime)
  public participatedSuSWorkshopLearningToLearn: Maybe<DateTime>;

  @Field(type => DateTime)
  public carriedOutSuSWorkshopLearningToLearn: Maybe<DateTime>;

  @Field(type => DateTime)
  public participatedSuSWorkshopMentalStrength: Maybe<DateTime>;

  @Field(type => DateTime)
  public carriedOutSuSWorkshopMentalStrength: Maybe<DateTime>;

  @Field(type => DateTime)
  public participatedSusWorkshopInnerStrenght: Maybe<DateTime>;

  @Field(type => DateTime)
  public carriedOutSusWorkshopInnerStrenght: Maybe<DateTime>;

  // ÜL Workshops
  @Field(type => DateTime)
  public participatedULTrainingOSCompanion: Maybe<DateTime>;

  @Field(type => DateTime)
  public participatedULWorkshopCompentenceOne: Maybe<DateTime>;

  @Field(type => DateTime)
  public participatedULWorkshopCompentenceTwo: Maybe<DateTime>;

  @Field(type => DateTime)
  public participatedULWorkshopMotivation: Maybe<DateTime>;

  @Field(type => DateTime)
  public participatedULWorkshopToDos: Maybe<DateTime>;

  @Field(type => DateTime)
  public participatedULWorkshopStress: Maybe<DateTime>;

  @Field(type => DateTime)
  public participatedULWorkshopCommunication: Maybe<DateTime>;

  @Field(type => DateTime)
  public participatedULWorkshopStorytelling: Maybe<DateTime>;

  @Field(type => DateTime)
  public participatedULWorkshopLanguage: Maybe<DateTime>;

  @Field(type => DateTime)
  public participatedULWorkshopDidacticReduction: Maybe<DateTime>;

  @Field(type => DateTime)
  public participatedULHeftCampusKurve: Maybe<DateTime>;

  public static workshopTypes = {
    participatedOSWorkshopOneRoleModel: 'Vorbild sein',
    participatedOSWorkshopTwoChallenges: 'Umgang mit Herausforderungen',
    participatedSuSWorkshopLearningToLearn: 'Lernen lernen',
    participatedSuSWorkshopMentalStrength: 'Mentale Stärke',
    participatedSusWorkshopInnerStrenght: 'Deine innere Kraft',
    participatedULTrainingOSCompanion: 'Einführung OS-Begleitung & Akquise',
    participatedULWorkshopCompentenceOne: 'Interkulturelle Kompetenzen - Teil 1',
    participatedULWorkshopCompentenceTwo: 'Interkulturelle Kompetenzen - Teil 2',
    participatedULWorkshopMotivation: 'Motivationsworkshop...gar kein Bock!',
    participatedULWorkshopToDos: "Ich & meine ToDo's",
    participatedULWorkshopStress: 'Wenn der Stress dich stresst',
    participatedULWorkshopCommunication: 'Kann man nicht nicht kommunizieren?',
    participatedULWorkshopStorytelling: 'Identifikation & Motivation durch Storytelling',
    participatedULWorkshopLanguage: 'Sprache & Sprachsensibilität im Fachunterricht',
    participatedULWorkshopDidacticReduction: 'Diaktische Reduktion',
    participatedULHeftCampusKurve: 'CHANCENhefte, CHANCENcampus & CHANCENkurve',
  };

  public static workshopMigration: Record<
    string,
    {
      training: string;
      carriedOut: Maybe<string>;
    }
  > = {
    'Vorbild sein': {
      training: 'participatedOSWorkshopOneRoleModel',
      carriedOut: 'carriedOutOSWorkshopOneRoleModel',
    },
    'Umgang mit Herausforderungen': {
      training: 'participatedOSWorkshopTwoChallenges',
      carriedOut: 'carriedOutOSWorkshopTwoChallenges',
    },
    'Lernen lernen': {
      training: 'participatedSuSWorkshopLearningToLearn',
      carriedOut: 'carriedOutSuSWorkshopLearningToLearn',
    },
    'Mentale Stärke': {
      training: 'participatedSuSWorkshopMentalStrength',
      carriedOut: 'carriedOutSuSWorkshopMentalStrength',
    },
    'Deine innere Kraft': {
      training: 'participatedSusWorkshopInnerStrenght',
      carriedOut: 'carriedOutSusWorkshopInnerStrenght',
    },
    'Einführung OS-Begleitung & Akquise': {
      training: 'participatedULTrainingOSCompanion',
      carriedOut: null,
    },
    'Interkulturelle Kompetenzen - Teil 1': {
      training: 'participatedULWorkshopCompentenceOne',
      carriedOut: null,
    },
    'Interkulturelle Kompetenzen - Teil 2': {
      training: 'participatedULWorkshopCompentenceTwo',
      carriedOut: null,
    },
    'Motivationsworkshop...gar kein Bock!': {
      training: 'participatedULWorkshopMotivation',
      carriedOut: null,
    },
    "Ich & meine ToDo's": {
      training: 'participatedULWorkshopToDos',
      carriedOut: null,
    },
    'Wenn der Stress dich stresst': {
      training: 'participatedULWorkshopStress',
      carriedOut: null,
    },
    'Kann man nicht nicht kommunizieren?': {
      training: 'participatedULWorkshopCommunication',
      carriedOut: null,
    },
    'Identifikation & Motivation durch Storytelling': {
      training: 'participatedULWorkshopStorytelling',
      carriedOut: null,
    },
    'Sprache & Sprachsensibilität im Fachunterricht': {
      training: 'participatedULWorkshopLanguage',
      carriedOut: null,
    },
    'Diaktische Reduktion': {
      training: 'participatedULWorkshopDidacticReduction',
      carriedOut: null,
    },
    'CHANCENhefte, CHANCENcampus & CHANCENkurve': {
      training: 'participatedULHeftCampusKurve',
      carriedOut: null,
    },
  };
}

/**
 * @entity KontoInformationen
 */
@Entity()
export class KontoInformationen extends AdornisEntity {
  public static override _class = 'KontoInformationen';

  @Field(type => String)
  public kontoinhaber!: string;

  // * We can't use iban() validator right now, because that would stop a lot of entities without a IBAN from being saveable.
  // * At the moment we only want to validate the IBAN if it is set.
  @validate(({ value, key, source }) => {
    if (!value || value === '') {
      return;
    }
    if (!validateIban(value)) {
      throw new ValidationError(`Bitte gebe eine korrekte IBAN ein!`, {
        key: 'iban',
        source,
        translationKey: 'ibanEmail',
      });
    }
  })
  @Field(type => String)
  public iban!: string;

  @Field(type => String)
  public bic!: string;

  @Field(type => String)
  public kreditinstitut!: string;
}

/**
 * @entity Unterlagen
 */
@Entity()
export class Unterlagen extends AdornisEntity {
  public static override _class = 'Unterlagen';

  @Field(type => DateTime)
  public vertragVersendet!: DateTime;

  @Field(type => DateTime)
  public vertragErhalten!: DateTime;

  @Field(type => DateTime)
  public zusatzvereinbarungVersendet!: DateTime;

  @Field(type => DateTime)
  public zusatzvereinbarungErhalten: Maybe<DateTime>;

  @Field(type => Boolean)
  public selbstverplichtungserklaerungErhalten!: boolean;

  @Field(type => Boolean)
  public datenschutzerklaerungErhalten!: boolean;

  @Field(type => DateTime)
  public fuehrungzeugnisGueltigAb!: DateTime;

  @Field(type => DateTime)
  public fuehrungzeugnisGueltigBis!: DateTime;

  @Field(type => Boolean)
  public vereinbarungUrheberrechtErhalten!: boolean;

  @Field(type => Boolean)
  public byodFormularErhalten!: boolean;

  @Field(type => DateTime)
  public ersthelferkursAbsolviertAm?: Maybe<DateTime>;

  @Field(type => Boolean)
  public aktuelleMasernimpfungNachgewiesen!: boolean;

  @Field(type => Boolean)
  public einwilligungserklaerungMasernimpfungNachweisErhalten!: boolean;

  @Field(type => String)
  public personalNummer!: string;

  @Field(type => String)
  public kreditorenNummer!: string;

  @Field(type => String, { default: v => v ?? '' })
  public kommunikationMitUL!: string;

  @Field(type => String, { default: v => v ?? '' })
  public sonstigeVermerke!: string;
}

/**
 * @entity Onboarding
 */
@Entity()
export class Onboarding extends AdornisEntity {
  public static override _class = 'Onboarding';

  constructor(doc: PartialEntityData<Onboarding>) {
    super(doc);
  }

  @Field(type => DateTime)
  public einarbeitungAbsolviertAm: Maybe<DateTime>;

  @Field(type => DateTime)
  public onboardingAkademieAbsolviertAm: Maybe<DateTime>;

  @Field(type => DateTime)
  public feedbackquizCompletedAt: Maybe<DateTime>;

  @Field(type => DateTime)
  public feedbacktalkCompletedAt: Maybe<DateTime>;

  @Field(type => DateTime)
  public trainingFinishedAt: Maybe<DateTime>;

  @Field(type => DateTime)
  public schoolSpecificTrainingFinishedAt: Maybe<DateTime>;

  public static onboardinTypes = {
    einarbeitungAbsolviertAm: 'CHANCENspirit (CHANCENakademie)',
    onboardingAkademieAbsolviertAm: 'Einführung der Lernkaskade (CHANCENakademie)',
    feedbackquizCompletedAt: 'Feedback-Quiz',
    feedbacktalkCompletedAt: 'Feedbackgespräch',
    trainingFinishedAt: 'Einarbeitung abgeschlossen',
    schoolSpecificTrainingFinishedAt: 'Schulspezifische Einarbeitung',
  };
}

export enum MitarbeiterState {
  GUEST = 'guest',
  COMPLETE = 'complete',
}

/**
 * @entity Mitarbeiter
 */
@Versioned({
  customName: 'Mitarbeiter:in',
  ignoreFields: {
    password: 1,
    savedCampusSectionIDs: 1,
    lastActivity: 1,
    lastPasswordChange: 1,
    createdBy: 1,
    createdAt: 1,
    isOnline: 1,
    ios_sub: 1,
    nativePermissions: 1,
    lastCampusLogin: 1,
  },
})
@Entity()
export class Mitarbeiter extends ChancenwerkUser {
  public static override _class = 'Mitarbeiter';

  public static override readableName = 'Mitarbeiter:in';

  public static override get allFields() {
    return selectionSet(() => this, 8);
  }

  public get state(): MitarbeiterState {
    if (this.guestAccount) return MitarbeiterState.GUEST;
    return MitarbeiterState.COMPLETE;
  }

  constructor(doc: PartialEntityData<Mitarbeiter>) {
    super(doc);
  }

  @Field(type => Boolean, {
    default: v => v ?? false,
  })
  public guestAccount: Maybe<boolean>;

  @Field(type => String)
  public override vorname?: string;

  @Field(type => String)
  public override nachname?: string;

  @Field(type => String)
  public employeeID?: Maybe<string>;

  @Field(type => String)
  public staatsangehoerigkeit?: string;

  @validate(({ value, key, source }) => {
    if (value && !emailRegexCheck(value as string))
      throw new ValidationError(`${value as string} ist keine gültige E-Mail.`, { key, source });
  })
  @Field(type => String)
  public emailChancenwerk?: string;

  @Field(type => String)
  public festnetz?: string;

  @Field(type => String)
  public handynummerChancenwerk?: string;

  @Field(type => String)
  public hochschule?: string;

  @Field(type => String)
  public studiengang?: string;

  @Field(type => Int)
  public grade?: number;

  @Field(type => String)
  public gradeExtraInfo?: string;

  @Field(type => Boolean, { default: v => v ?? false })
  public kontaktaufnahmeEmail!: boolean;

  @Field(type => Boolean, { default: v => v ?? false })
  public kontaktaufnahmeHandynummer?: boolean;

  @Field(type => Boolean, { default: v => v ?? false })
  public kontaktaufnahmeFestnetznummer?: boolean;

  @Field(type => [CRVertrag], { default: v => v ?? [] })
  public vertraege!: CRVertrag[];

  @Field(type => Unterlagen, { default: v => v ?? new Unterlagen({}) })
  public unterlagen!: Unterlagen;

  @Field(type => KontoInformationen, { default: v => v ?? new KontoInformationen({}) })
  public kontoInformationen!: KontoInformationen;

  @Field(type => Onboarding, { default: v => v ?? new Onboarding({}) })
  public onboarding!: Onboarding;

  @Field(type => WorkshopsAndTrainingInformation, { default: v => v ?? new WorkshopsAndTrainingInformation({}) })
  public workshopsAndTrainingInformation: Maybe<WorkshopsAndTrainingInformation>;

  @Field(type => [String], { default: v => v ?? [] })
  public subjectExpertise: Maybe<string[]>;

  @Field(type => [WorkAvailablility], { default: v => v ?? [] })
  public workAvailabilities: Maybe<WorkAvailablility[]>;

  public ensureWorkAvailabilities() {
    if (!this.workAvailabilities) this.workAvailabilities = [];
    return this.workAvailabilities;
  }

  public getWorkAvailability(weekday: Weekday) {
    const found = this.ensureWorkAvailabilities().find(w => w.weekday === weekday);
    if (!found) {
      const newWorkAvailability = new WorkAvailablility({
        weekday,
      });
      this.workAvailabilities?.push(newWorkAvailability);
      return newWorkAvailability;
    }
    return found;
  }

  static override async beforeCreate(entity: Mitarbeiter) {
    if (!entity.employeeID) entity.employeeID = (await ChancenwerkCounter.getIncrementingEmployeeID()()).toString();

    return super.beforeCreate(entity);
  }

  protected override async saveMailEventLog(error?: Error) {
    await new MailEventLog({
      userID: this._id,
      mailType: MailEventLogType.MITARBEITER_REGISTER,
      successful: !error,
      sendMailError: error?.message,
      emailAddress: this.email,
    }).create();
  }

  protected override async getComposedRegisterMail() {
    return Message.compose({
      subject: 'Willkommen auf dem CHANCENcampus',
      html: registerEmployee({
        username: this.employeeID ?? '',
        confirmationUrl: (await Mitarbeiter.getPasswordChangeLink(this._id, 'chancennetz')).toString(),
      }),
    });
  }

  /**
   * **Attention** this will throw if contract is not empty, you will need to catch it
   */
  removeContract(contractIdx: number) {
    const vertrag = this.vertraege[contractIdx];
    if (!vertrag) throw new Error(`Es wurde kein Vertrag mit dem Index ${contractIdx} gefunden.`);
    if (!vertrag.taetigkeitenIDs?.length) this.vertraege.splice(contractIdx, 1);
    else throw new Error('Der Vertrag muss leer sein um diesen zu löschen.');
  }

  public static countVertragEndingDate(
    einsatzstelle: string,
    mitarbeiter: Mitarbeiter[],
    predicateID?: string,
    time?: DateTime,
  ) {
    return mitarbeiter.reduce((acc, schuKo) => {
      const t = schuKo.findActiveTaetigkeitByEinsatzstelle(einsatzstelle, predicateID, time);
      const vertagEnd = t?.endDate;
      return acc + (vertagEnd ? 1 : 0);
    }, 0);
  }

  findActiveTaetigkeitByEinsatzstelle(einsatzstelle: string, predicateID?: string, time?: DateTime) {
    const possible = this.vertraege
      .flatMap(v => v.taetigkeiten)
      .filter(t => t?.objectID === einsatzstelle && (!predicateID || t.predicateID === predicateID));
    if (!possible) return null;

    const now = (time ?? DateTime.now()).endOf('day');
    const active = possible.filter(t => {
      if (t?.startDate && t?.startDate > now) return false;
      if (t?.endDate && t?.endDate < now) return false;
      return true;
    });

    if (!active.length) return null;
    return active[0];
  }

  private static async _leKoAggregation(
    kpfID: string | string[],
    onlyReturnCount: boolean,
    skip: number,
    limit: number,
    match?: any,
  ) {
    // * By searching through the semantic relationships we can find all employees that are currently active in a KPF
    // * way faster than searching through all employees and checking their roles and filtering afterwards.
    const collection = await getRawCollection<SemanticRelationship>(SemanticRelationship._collectionName);
    const agg = await collection
      .aggregate([
        // * 1. find all active LEKOs
        {
          $match: {
            $and: [
              { predicateID: Role.LEKO },
              typeof kpfID === 'string' ? { objectID: kpfID } : { objectID: { $in: kpfID } },
              Taetigkeit.isActiveMongoSelector(),
            ],
          },
        },

        // * 2. fetch the user
        {
          $lookup: {
            from: Mitarbeiter._collectionName,
            localField: 'subjectID',
            foreignField: '_id',
            as: 'employee',
          },
        },

        // * 3. user array to user object
        {
          $unwind: '$employee',
        },

        // * 4. pull the users out so that we have a array of users
        {
          $replaceRoot: { newRoot: '$employee' },
        },

        // * 5. match the publish filter
        ...(match
          ? [
              {
                $match: match,
              },
            ]
          : []),

        // * 6. count or sort, skip, limit
        ...(onlyReturnCount ? [{ $count: 'count' }] : [{ $sort: { nachname: 1 } }, { $skip: skip }, { $limit: limit }]),
      ])
      .toArray();

    if (onlyReturnCount) {
      return ((agg[0] ?? { count: 0 }) as any).count as number;
    }

    return agg as any as EntityData<Mitarbeiter>[];
  }

  static _leKoAggregationSub(
    kpfID: string,
    onlyReturnCount: boolean,
    skip: number,
    limit: number,
    enablePublishFilter: boolean = true,
  ) {
    const handle = getCollectionHandle<Mitarbeiter>(this._collectionName);
    return from(publishFilter(this._class)).pipe(
      switchMap(publishFilter => {
        const selector = {};
        return handle
          .watchQuery(
            publishFilter && enablePublishFilter ? { $and: [publishFilter, selector] } : selector,
            onlyReturnCount ? {} : { skip, limit },
          )
          .pipe(mapTo({ ...selector, ...(enablePublishFilter ? publishFilter : {}) }));
      }),
      switchMap(async docs => {
        if (!onlyReturnCount) {
          const users = (await this._leKoAggregation(kpfID, onlyReturnCount, skip, limit, docs)) as Array<
            EntityData<Mitarbeiter>
          >;
          return users.map(u => constructValue(u) as Mitarbeiter);
        }

        const usersCount = (await this._leKoAggregation(kpfID, onlyReturnCount, skip, limit, docs)) as number;
        return usersCount;
      }),
    );
  }

  @Query(type => [Mitarbeiter])
  public static getPublicLeKoByKPF(@Arg('kpfID', type => String) kpfID: string) {
    return async (gqlFields: BaseQLSelectionSet<Mitarbeiter>) => {
      const user = await getUserFromContext<ChancenwerkUser>({
        roles: ChancenRole.allFields,
      });
      if (!user) {
        throw new Error('unauthorized');
      }

      if (
        !user.hasPermission(ChancenPermission.board.VIEW_ATTENDANCE) &&
        !user.hasPermission(ChancenPermission.board.EXPORT_ATTENDANCE)
      ) {
        throw new Error('unauthorized');
      }

      const users = (await this._leKoAggregation(kpfID, false, 0, 1000, false)) as EntityData<Mitarbeiter>[];
      return users.map(u => constructValue(u) as Mitarbeiter);
    };
  }

  @Subscription(type => Int)
  public static subscribeNumberOfLeKoByKPF(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('kpfID', type => String) kpfID: string,
  ) {
    return (gqlFields: void) => this._leKoAggregationSub(kpfID, true, skip, limit) as Observable<number>;
  }

  @Mutation(type => Mitarbeiter)
  static getMitarbeiterFromSoftgardenKey(@Arg('key', type => String) key: string) {
    return async (gqlFields: BaseQLSelectionSet<Mitarbeiter>) => {
      return getMitarbeiterFromSoftgardenKeyFromCache(key);
    };
  }
}

if (A.isServer) {
  void (async () => {
    const coll = await getRawCollection<Mitarbeiter>(Mitarbeiter._collectionName);
    await coll.createIndex({ employeeID: 1 });
  })();
}
